import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { SubSink } from 'subsink2';
import moment from 'moment';
import {map} from 'rxjs/operators';
import { AuditTrailSelectors } from '../../../settings-controller/applet-audit-trail/selectors';
import { AppletAuditTrailContainer, UserProfileService } from 'blg-akaun-ts-lib';
import { AuditTrailState } from '../../../settings-controller/applet-audit-trail/states/applet-audit-trail.states';

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

interface DataItem {
  [key: string]: any;
}

interface Payload {
  old_data: {
    [tableName: string]: DataItem;
  };
  delete_data: {
    [tableName: string]: DataItem;
  };
  new_data: {
    [tableName: string]: DataItem;
  };
  auth_token_fields: {

  }
}

interface Difference {
  field: string;
  oldValue: any;
  newValue: any;
  tableName: string;
}

@Component({
  selector: 'applet-audit-trail-edit',
  templateUrl: './applet-audit-trail-edit.component.html',
  styleUrls: ['./applet-audit-trail-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]

})
export class AppletAuditTrailEditComponent extends ViewColumnComponent {

  protected subs = new SubSink();

  protected compName = 'Audit Trail View';
  protected readonly index = 1;
  protected localState: LocalState;

  apiVisa = AppConfig.apiVisa;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.deactivateAdd);
  readonly deactivateReturn$ = this.componentStore.select(state => state.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => state.selectedIndex);

  prevIndex: number;
  protected prevLocalState: any;
  public form: FormGroup;
  formattedDescription;

  auditTrail = this.store.select(AuditTrailSelectors.selectAuditTrail);
  

  constructor(
    private viewColFacade: PermissionFacade,
    private readonly componentStore: ComponentStore<LocalState>,
    protected readonly store: Store<AuditTrailState>,
    private  userProfileService: UserProfileService,
  ) {
    super();
  }

  ngOnInit() {
    this.subs.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState(a);
    });
    this.subs.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subs.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);
  
    this.form = new FormGroup({
      table_name: new FormControl(''),
      action: new FormControl(''),
      description: new FormControl(''),
      action_date: new FormControl(''),
      status: new FormControl(''),
    });
  
    this.subs.sink = this.store.select(AuditTrailSelectors.selectAuditTrail).subscribe(async (response: AppletAuditTrailContainer) => {
      if (response) {
        const payload = response.bl_applet_audit_trail.message_json as any;
        this.formattedDescription = await this.compareData(payload.payload);
        this.form.patchValue({
          table_name: response.bl_applet_audit_trail.foreign_table,
          action: response.bl_applet_audit_trail.action,
          action_date: moment(response.bl_applet_audit_trail.date_created).format('YYYY-MM-DD'), 
          status: response.bl_applet_audit_trail.status
        });
      }
    });
  }
  
  formatDate(dateTime: string): string {
    return moment(dateTime).format('YYYY-MM-DD');
  }


  async compareData(payload: Payload): Promise<string> {
    const skipFields = ['revision', 'property_json', 'acl_config', 'acl_policy', 'guid'];
    let description = '';

    const oldTableName = this.isNotNullOrUndefined(payload?.old_data) ? Object.keys(payload.old_data)?.[0] : null;
    const newTableName = this.isNotNullOrUndefined(payload?.new_data) ? Object.keys(payload.new_data)?.[0] : null;
    const delete_data_table = this.isNotNullOrUndefined(payload?.delete_data) ? Object.keys(payload.delete_data)?.[0] : null;
    const auth_token_fields = payload.auth_token_fields as any;
    const actionByGuid = auth_token_fields?.subjectGuid;
    const actionByName = this.isNotNullOrUndefined(actionByGuid) ? await this.getName(actionByGuid) : "";
  
    if (this.isNotNullOrUndefined(delete_data_table)) {
      const deleteData = payload.delete_data[delete_data_table];
      description += `<b>Deleted a Record with the following Values:</b> <br>`;
      for (const key in deleteData) {
        if (skipFields.includes(key) || !this.isNotNullOrUndefined(deleteData[key]) || key.toLowerCase().includes('guid')) {
          continue;
        }
        let deleteDataValue = deleteData[key];
        if (moment(deleteDataValue, moment.ISO_8601, true).isValid()) {
          deleteDataValue = this.formatDate(deleteDataValue);
        }
        description += `<span style="color:blue">${key}</span>, Value:<span style="color:red"> ${deleteDataValue}</span><br>`;
      }
      description += `<span style="color:blue">Action By:</span>  ${actionByName}<br>`;
    } else if (this.isNotNullOrUndefined(oldTableName) && this.isNotNullOrUndefined(newTableName)) {
      if (oldTableName === newTableName) {
        const oldData = payload.old_data[oldTableName];
        const newData = payload.new_data[newTableName];
        description += `<b>Updated a Record with the following New Values:</b> <br>`;
        for (const key in oldData) {
          if (skipFields.includes(key) || key.toLowerCase().includes('guid')) {
            continue;
          }
  
          let oldValue = oldData[key];
          let newValue = newData[key];
  
          if (moment(oldValue, moment.ISO_8601, true).isValid() && moment(newValue, moment.ISO_8601, true).isValid()) {
            oldValue = this.formatDate(oldValue);
            newValue = this.formatDate(newValue);
          }
  
          if (oldValue !== newValue) {
            description += `<span style="color:blue">${key}</span>, Old Value: <span style="color:red">${oldValue || 'N/A'}</span>, New Value: <span style="color:green">${newValue || 'N/A'}</span> <br>`;
          }
        }
        description += `<span style="color:blue">Action By:</span>  ${actionByName}<br><br>`;

        description += `<b>Record Details:</b> <br>`;
        for (const key in newData) {
          if (skipFields.includes(key) || !this.isNotNullOrUndefined(newData[key]) || key.toLowerCase().includes('guid')) {
            continue;
          }
  
          let newValue = newData[key];
  
          if (moment(newValue, moment.ISO_8601, true).isValid()) {
            newValue = this.formatDate(newValue);
          }
            description += `<span style="color:blue">${key}</span>, Value: ${newValue || 'N/A'}<br>`;
        }
      }
    } else if (this.isNotNullOrUndefined(newTableName)) {
      const newData = payload.new_data[newTableName];
      description += `<b>Created a Record with the following Values:</b> <br>`;
      for (const key in newData) {
        if (skipFields.includes(key) || key.toLowerCase().includes('guid') || !this.isNotNullOrUndefined(newData[key])) {
          continue;
        }
        let newValue = newData[key];
        if (moment(newValue, moment.ISO_8601, true).isValid()) {
          newValue = this.formatDate(newValue);
        }
        description += `<span style="color:blue">${key}</span>, Value: <span style="color:green">${newValue}</span><br>`;
      }
      description += `<span style="color:blue">Action By:</span>  ${actionByName}<br>`;
    }
    return description.trim();
  }
  

  getName(subjectGuid: string): Promise<string> {
    return this.userProfileService.getProfileName(AppConfig.apiVisa, subjectGuid.toString()).pipe(
      map((profileData) => profileData.data)
    ).toPromise();
  }
  

  onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  isNotNullOrUndefined(value) {
    return value !== null && value !== undefined;
  }
}
